// minVisibleDuration — hold a transient indicator on screen long enough to be
// SEEN, even when the work it reports finished sooner.
//
// The bug this exists for: double-clicking a blueprint card hydrates a graph.
// On a warm cache that is ~60 ms, so the only feedback — a spinner keyed on an
// in-flight flag — mounted and unmounted inside one or two frames. The user
// sees nothing, cannot tell the double-click even registered, and clicks again.
// A fast action with invisible feedback reads exactly like a dead control.
//
// So the indicator's lifetime is decoupled from the work's: the work is awaited
// normally (and its result and its errors are passed through untouched — this
// must never change what the caller observes), while the FLAG stays raised for
// at least `minMs`. macOS does the same thing with the dock bounce: an app that
// launches instantly still bounces once, because the bounce answers "your click
// landed", not "the app is slow".
//
// Generic on purpose. Every content-browser action with a sub-second happy path
// and a visible busy state has this shape (open a graph, apply a material,
// promote a default), so the rule lives here once with the duration as a
// parameter, rather than as a setTimeout copied into each call site.

// One dock-bounce cycle. Long enough to register as motion, short enough that
// it never feels like an imposed delay — the work is usually still running.
export const MIN_ACK_MS = 420

/**
 * Run `work`, keeping a caller-owned flag raised for at least `minMs`.
 *
 * @param {() => Promise<T>|T} work      the real action
 * @param {(on: boolean) => void} setFlag raise/lower the indicator
 * @param {object} [opts]
 * @param {number} [opts.minMs=MIN_ACK_MS]
 * @param {() => number} [opts.now]      injectable clock (tests)
 * @param {(ms:number) => Promise<void>} [opts.sleep] injectable timer (tests)
 * @returns {Promise<T>} whatever `work` returned; rejections propagate.
 */
export async function withMinVisible(work, setFlag, opts = {}) {
  const {
    minMs = MIN_ACK_MS,
    now = () => Date.now(),
    sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
  } = opts

  const started = now()
  setFlag(true)
  try {
    return await work()
  } finally {
    // Elapsed is measured across the whole await, so slow work adds nothing:
    // the floor applies to the INDICATOR, never to the action.
    const remaining = minMs - (now() - started)
    if (remaining > 0) await sleep(remaining)
    setFlag(false)
  }
}
